const STORAGE_PREFIX = "gwin_knowledge_check";

function safeKeyPart(value) {
  return String(value ?? "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^a-zA-Z0-9_-]/g, "")
    .slice(0, 120);
}

function buildStorageKey({
  questId,
  subquestId,
  levelKey,
  questionKey,
}) {
  return [
    STORAGE_PREFIX,
    safeKeyPart(questId),
    safeKeyPart(subquestId),
    safeKeyPart(levelKey),
    safeKeyPart(questionKey),
  ].join("_");
}

function getDefaultRecord() {
  return {
    wrongAttempts: 0,
    completedCorrectly: false,
    correctAnswerId: null,
    correctMatches: [],
  };
}

export function getKnowledgeCheckProgress(context) {
  if (typeof window === "undefined") {
    return getDefaultRecord();
  }

  const key = buildStorageKey(context);

  try {
    const raw = localStorage.getItem(key);

    if (!raw) {
      return getDefaultRecord();
    }

    const parsed = JSON.parse(raw);

    return {
      wrongAttempts:
        Number(parsed?.wrongAttempts) || 0,

      completedCorrectly:
        parsed?.completedCorrectly === true,

      correctAnswerId:
        parsed?.correctAnswerId ?? null,

      correctMatches:
        Array.isArray(parsed?.correctMatches)
          ? parsed.correctMatches
          : [],
    };
  } catch {
    return getDefaultRecord();
  }
}

export function saveKnowledgeCheckProgress(
  context,
  changes
) {
  if (typeof window === "undefined") return;

  const key = buildStorageKey(context);

  try {
    const current =
      getKnowledgeCheckProgress(context);

    const next = {
      ...current,
      ...changes,
    };

    localStorage.setItem(
      key,
      JSON.stringify(next)
    );

    return next;
  } catch {
    return null;
  }
}

export function incrementKnowledgeCheckWrongAttempt(
  context
) {
  const current =
    getKnowledgeCheckProgress(context);

  const wrongAttempts =
    current.wrongAttempts + 1;

  return saveKnowledgeCheckProgress(
    context,
    {
      wrongAttempts,
    }
  );
}

export function saveCorrectSelectAnswer(
  context,
  answerId
) {
  return saveKnowledgeCheckProgress(
    context,
    {
      completedCorrectly: true,
      correctAnswerId: String(answerId),
    }
  );
}

export function saveCorrectDragAnswer(
  context,
  matches
) {
  return saveKnowledgeCheckProgress(
    context,
    {
      completedCorrectly: true,

      correctMatches:
        Array.isArray(matches)
          ? matches
          : [],
    }
  );
}

export function clearLevelKnowledgeCheckProgress({
  questId,
  subquestId,
  levelKey,
} = {}) {
  if (typeof window === "undefined") return 0;

  const questPart = safeKeyPart(questId);
  const subquestPart = safeKeyPart(subquestId);
  const levelPart = safeKeyPart(levelKey);

  /*
   * Do not clear anything unless we have the exact level identity.
   * This prevents accidentally wiping other levels.
   */
  if (!questPart || !subquestPart || !levelPart) {
    return 0;
  }

  const keyPrefix = [
    STORAGE_PREFIX,
    questPart,
    subquestPart,
    levelPart,
  ].join("_") + "_";

  const lowerKeyPrefix = keyPrefix.toLowerCase();

  let removedCount = 0;

  const clearFromStore = (store) => {
    if (!store) return;

    const keys = [];

    for (let index = 0; index < store.length; index += 1) {
      const key = store.key(index);

      if (key) {
        keys.push(key);
      }
    }

    keys.forEach((key) => {
      if (!String(key).toLowerCase().startsWith(lowerKeyPrefix)) {
        return;
      }

      try {
        store.removeItem(key);
        removedCount += 1;
      } catch {}
    });
  };

  clearFromStore(window.localStorage);
  clearFromStore(window.sessionStorage);

  return removedCount;
}